import React, { Component } from 'react'
import styled from 'styled-components'
import { get } from 'lodash'
import Shell from '../EditorTools/Shell'
import Button from '../../../../components/Button'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import { wpAxios } from '../../../../config/axiosResolver'
import ConfigResolver from '../../../../config/ConfigResolver'

const config = ConfigResolver.config()

/* Measured over 4,019 published titles: median 62 characters, p90 88. Search
   results truncate around 60, so the two agree closely enough that one number
   serves both — which is why the hint below is a nudge and not a rule. */
const IDEAL_CHARS = 62
const LONG_CHARS = 88

const Panel = styled.div`
  display: flex;
  flex-direction: column;

  ${p => p.$embedded
    ? `
      flex: 1;
      min-height: 0;
      padding: 18px 30px 22px;
    `
    : `
      width: 760px;
      max-width: 92vw;
      max-height: 88vh;
      padding: 30px 34px 24px;
    `}
`

const Title = styled.div`
  font-family: Hind;
  font-size: 22px;
  color: #000;
`

const Sub = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-weight: 300;
  color: #9F9E9B;
  margin: 4px 0 18px;
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: 4px;
  position: relative;
`

const Current = styled.div`
  border-left: 2px solid #EDEAE8;
  padding: 0 0 0 12px;
  margin-bottom: 22px;
`

const Label = styled.div`
  font-family: Hind;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #A09A96;
  margin-bottom: 5px;
`

const CurrentText = styled.div`
  font-family: Hind;
  font-size: 16px;
  font-weight: 300;
  color: #55575A;
  line-height: 1.4;
`

/* Each candidate is a row you select rather than a button you fire. Three
   titles are meant to be compared, and a per-row "Use" turns comparison into a
   sequence of irreversible-feeling clicks. */
const Candidate = styled.div`
  border: 1px solid ${p => p.$selected ? '#ECB595' : '#E4E0DC'};
  background: ${p => p.$selected ? '#FDF8F4' : '#FFF'};
  border-radius: 3px;
  padding: 13px 15px;
  margin-bottom: 10px;
  cursor: pointer;

  &:hover { border-color: #ECB595; }
`

const CandidateText = styled.div`
  font-family: Hind;
  font-size: 16px;
  color: #404040;
  line-height: 1.4;
`

const Meta = styled.div`
  font-family: Hind;
  font-size: 12px;
  color: ${p => p.$long ? '#B4776F' : '#9F9E9B'};
  margin-top: 6px;
`

const Empty = styled.div`
  padding: 34px 0 30px;
  text-align: center;
`

const EmptyText = styled.div`
  font-family: Hind;
  font-size: 15px;
  font-weight: 300;
  color: #55575A;
  line-height: 1.6;
  max-width: 430px;
  margin: 0 auto 22px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 18px;
  margin-top: 4px;
  border-top: 1px solid #EDEAE8;
`

const Spacer = styled.div`flex: 1;`

const Cancel = styled.div`
  font-family: Hind;
  font-size: 15px;
  font-weight: 300;
  color: #ECB595;
  cursor: pointer;
`

const Notice = styled.div`
  font-family: Hind;
  font-size: 13px;
  font-weight: 300;
  color: ${p => p.tone === 'bad' ? '#B4776F' : '#777'};
  background: #FAFAFA;
  border-left: 2px solid ${p => p.tone === 'bad' ? '#B4776F' : '#ECB595'};
  padding: 9px 12px;
  margin-bottom: 16px;
`

const Refused = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-weight: 300;
  color: #9F9E9B;
  margin-top: 14px;
  line-height: 1.5;
`

/**
 * STY-624: draft candidate titles and choose one.
 *
 * Three candidates rather than one, because the corpus carries two shapes for
 * a wedding feature — a descriptive noun phrase that front-loads what people
 * search for, and an editorial hook that does not — and which suits a given
 * wedding is a judgement an editor should make rather than receive.
 *
 * Nothing is saved until Use. Drafting only returns text.
 */
class TitleDraft extends Component {
  constructor (props) {
    super(props)

    this.state = {
      titles: [],
      refused: [],
      selected: null,
      loading: false,
      saving: false,
      notice: null,
      tone: 'info'
    }
  }

  // Nothing runs on arrival, as with the opening writer. Clicking a tab is not
  // the same as asking for a paid model call.

  componentDidUpdate (prevProps, prevState) {
    // Embedded, the workspace owns the modal, so it is the only thing that can
    // refuse to close over a write in flight.
    const was = prevState.loading || prevState.saving
    const now = this.state.loading || this.state.saving

    if (was !== now && this.props.onBusy) this.props.onBusy(now)
  }

  generate () {
    if (this.props.locked) return Promise.resolve()

    const { submissionId, flushSaves } = this.props

    if (this.state.loading) return

    this.setState({ loading: true, notice: null })

    // Titles are drafted from the STORED submission, so a save still queued
    // from the field the editor just left has to land first.
    const ready = flushSaves ? flushSaves() : Promise.resolve()

    ready.then(
      () => {
        // Rechecked PAST the flush. The status the flush was waiting for can
        // be the one that locks the title: an editor who sets a submission to
        // quick or denied and goes straight to Copy tools passed the guard
        // above while that PUT was still in flight, and this posted a paid
        // request for candidates the Use button would then refuse to apply.
        if (this.props.locked) {
          this.setState({
            loading: false,
            tone: 'info',
            notice: 'This submission\u2019s title is locked by its status. Nothing was drafted.'
          })

          return
        }

        return wpAxios
          .post(`${config.siteUrl}/api/v2/submissions/${submissionId}/ai-draft-title`)
          .then((res) => {
            const data = get(res, 'data.data', {})
            const titles = data.titles || []

            this.setState({
              loading: false,
              titles: titles,
              refused: data.rejected || [],
              selected: titles.length ? 0 : null,
              tone: 'info',
              notice: titles.length
                ? null
                : 'Every candidate was refused by the guard. Try again — the wording differs each run.'
            })
          })
          .catch((err) => {
            this.setState({
              loading: false,
              tone: 'bad',
              notice: get(err, 'response.data.message')
              || 'Could not reach the title writer. Nothing was changed.'
            })
          })
      },
      () => {
        this.setState({
          loading: false,
          tone: 'bad',
          notice: 'Your last edit has not been saved yet, so nothing was drafted. Save that change first.'
        })
      }
    )
  }

  use () {
    // The button is disabled too. Refusing here as well because this is the
    // method that actually writes, and a disabled button is a UI state.
    if (this.props.locked) return Promise.resolve()

    const { onSave, handleClose } = this.props
    const { titles, selected, saving } = this.state

    if (saving || selected === null || !titles[selected]) return

    this.setState({ saving: true, notice: null })

    Promise.resolve(onSave(titles[selected]))
      .then(() => {
        this.setState({ saving: false })
        handleClose()
      })
      .catch((err) => {
        this.setState({
          saving: false,
          tone: 'bad',
          notice: get(err, 'response.data.message')
            || 'The title could not be saved. Nothing was changed.'
        })
      })
  }

  render () {
    const { open, handleClose, currentTitle, locked } = this.props
    const { titles, refused, selected, loading, saving, notice, tone } = this.state

    const busy = loading || saving

    return (
      <ErrorBoundary>
        <Shell embedded={this.props.embedded} open={open} handleClose={busy ? () => {} : handleClose}>
          <Panel $embedded={this.props.embedded}>
            <Title>Title</Title>
            <Sub>
              Three options, written from the submission. Pick one, or close and
              keep what you have — nothing is saved until you choose Use.
            </Sub>

            <Body>
              {notice && <Notice tone={tone}>{notice}</Notice>}

              {currentTitle ? (
                <Current>
                  <Label>Current title</Label>
                  <CurrentText>{currentTitle}</CurrentText>
                  <Meta>{currentTitle.length} characters</Meta>
                </Current>
              ) : null}

              {/* Stays up WHILE loading. Hiding it on `loading` removed the only
                  thing carrying a spinner, and the busy state locks the modal
                  at the same moment -- so the first request looked like the
                  panel had frozen for ten seconds with nothing to click. */}
              {!titles.length ? (
                <Empty>
                  <EmptyText>
                    Titles carry the search terms and still have to earn a click.
                    You will get a descriptive option and at least one hook, each
                    written only from what the couple submitted.
                  </EmptyText>
                  <Button
                    text={loading ? 'Writing titles' : 'Draft titles'}
                    width={190}
                    showLoader={loading}
                    disabled={loading || locked}
                    onClick={() => this.generate()}
                  />
                </Empty>
              ) : null}

              {titles.map((title, i) => (
                <Candidate
                  key={i}
                  $selected={selected === i}
                  onClick={() => busy ? null : this.setState({ selected: i })}
                >
                  <CandidateText>{title}</CandidateText>
                  <Meta $long={title.length > LONG_CHARS}>
                    {title.length} characters
                    {title.length > LONG_CHARS
                      ? ' — longer than most SMP titles, and search results may cut it'
                      : ` — house median is ${IDEAL_CHARS}`}
                  </Meta>
                </Candidate>
              ))}

              {refused.length ? (
                <Refused>
                  {refused.length} {refused.length === 1 ? 'candidate was' : 'candidates were'} refused
                  before you saw them: {refused.join('; ')}.
                </Refused>
              ) : null}
            </Body>

            <Footer>
              {titles.length ? (
                <Button
                  text='Draft three more'
                  width={175}
                  showLoader={loading}
                  disabled={busy || locked}
                  onClick={() => this.generate()}
                />
              ) : null}
              <Spacer />
              <Cancel onClick={() => busy ? null : handleClose()}>Cancel</Cancel>
              <Button
                text='Use this title'
                width={155}
                showLoader={saving}
                disabled={busy || locked || selected === null || !titles.length}
                onClick={() => this.use()}
              />
            </Footer>
          </Panel>
        </Shell>
      </ErrorBoundary>
    )
  }
}

export default TitleDraft
